import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { SiteContent } from '@/lib/types'

interface UseUpdateContentReturn {
  updateContent: (content: SiteContent) => Promise<boolean>
  saving: boolean
  error: string | null
}

/**
 * Hook for admin to update site content in database.
 * Only authenticated admins should use this hook.
 *
 * Validates content structure and required fields before saving.
 * Includes validation for phone number format (+...).
 *
 * @returns {UseUpdateContentReturn} Object containing:
 *   - updateContent: Async function to save content (returns boolean for success)
 *   - saving: Boolean indicating if save operation is in progress
 *   - error: Error message string or null
 *
 * @example
 * ```tsx
 * function AdminEditor() {
 *   const { updateContent, saving, error } = useUpdateContent()
 *
 *   const handleSave = async () => {
 *     const success = await updateContent(formData)
 *     if (success) {
 *       showNotification('Saved successfully')
 *     }
 *   }
 *
 *   return <Button onClick={handleSave} loading={saving}>Save</Button>
 * }
 * ```
 */
export function useUpdateContent(): UseUpdateContentReturn {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateContent = useCallback(async (content: SiteContent): Promise<boolean> => {
    setSaving(true)
    setError(null)

    try {
      // Validate required fields
      if (
        !content.event.coupleNames ||
        !content.event.invitationText ||
        !content.event.eventTitle ||
        !content.event.eventDate
      ) {
        throw new Error('Event details are required')
      }
      if (!content.location.name || !content.location.addressLine1) {
        throw new Error('Location details are required')
      }
      if (!content.contact.primaryPhoneNumber) {
        throw new Error('Primary phone number is required')
      }
      if (!content.invitation.storyText) {
        throw new Error('Invitation story text is required')
      }

      // Validate phone format (should start with +)
      if (
        content.contact.primaryPhoneNumber &&
        !content.contact.primaryPhoneNumber.startsWith('+')
      ) {
        throw new Error('Phone numbers should start with + (international format)')
      }

      const { error: updateError } = await supabase
        .from('site_content')
        .update({
          content: content as unknown as any,
          updated_at: new Date().toISOString(),
          updated_by: 'admin', // TODO: Get actual admin identifier
        })
        .eq('id', 'default')

      if (updateError) {
        throw updateError
      }

      setError(null)
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save content'
      setError(errorMessage)
      console.error('Error updating site content:', err)
      return false
    } finally {
      setSaving(false)
    }
  }, [])

  return {
    updateContent,
    saving,
    error,
  }
}
